/**
 * SavedViewsRow.jsx — Phase 121/123 — pin the current filter combo of any
 * Global Filter Bar as a one-click chip, capped at 5 server-side (LRU
 * eviction). Reused by the Home dashboard's TaskMasterFilterBar and the
 * Finance Ledger's Analytics Panel — each just points it at its own
 * `apiPath` + describes how to read/apply its own filter shape.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pin, X, Check } from "lucide-react";

export default function SavedViewsRow({ apiPath, currentFilters, onApply, testIdPrefix = "saved-view" }) {
  const [savedViews, setSavedViews] = useState([]);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(apiPath).then(({ data }) => setSavedViews(data || [])).catch(() => setSavedViews([]));
  }, [apiPath]);

  const applyView = async (v) => {
    onApply(v);
    try { await api.patch(`${apiPath}/${v.id}/touch`); } catch { /* non-critical */ }
  };

  const saveCurrentView = async () => {
    const name = draftName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post(apiPath, { name, ...currentFilters });
      const { data: fresh } = await api.get(apiPath);
      setSavedViews(fresh || []);
      setDraftName("");
      setNaming(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeView = async (id) => {
    setSavedViews((arr) => arr.filter((v) => v.id !== id));
    try { await api.delete(`${apiPath}/${id}`); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testIdPrefix}s-row`}>
      {savedViews.map((v) => (
        <span key={v.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium hover:bg-slate-200" data-testid={`${testIdPrefix}-${v.id}`}>
          <button type="button" onClick={() => applyView(v)} className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-apply-${v.id}`}>
            <Pin size={9} className="text-[#F54800]" /> {v.name}
          </button>
          <button type="button" onClick={() => removeView(v.id)} className="text-slate-400 hover:text-rose-600 p-0.5" title="Unpin" data-testid={`${testIdPrefix}-remove-${v.id}`}>
            <X size={10} />
          </button>
        </span>
      ))}
      {naming ? (
        <span className="inline-flex items-center gap-1">
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveCurrentView(); if (e.key === "Escape") { setNaming(false); setDraftName(""); } }}
            placeholder="View name…"
            maxLength={40}
            className="h-7 text-[11px] rounded-sm border border-slate-200 px-2 w-[130px]"
            data-testid={`${testIdPrefix}-name-input`}
          />
          <button type="button" onClick={saveCurrentView} disabled={saving || !draftName.trim()} className="text-emerald-600 hover:text-emerald-700 p-1" data-testid={`${testIdPrefix}-name-confirm`}>
            <Check size={13} />
          </button>
          <button type="button" onClick={() => { setNaming(false); setDraftName(""); }} className="text-slate-400 hover:text-slate-600 p-1" data-testid={`${testIdPrefix}-name-cancel`}>
            <X size={13} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium text-[#F54800] border border-dashed border-[#F54800]/40 hover:bg-[#FFF4EE]"
          data-testid={`${testIdPrefix}-pin-current`}
        >
          <Pin size={10} /> Pin current view
        </button>
      )}
    </div>
  );
}
